export type TranslationMap = Record<string, string>;

const ARABIC_TEXT_PATTERN = /[\u0600-\u06FF]/;

const normalizeApiBaseUrl = (baseUrl: string) => baseUrl.replace(/\/$/, '');

export const getApiBaseUrls = () => {
  const envBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (envBaseUrl) return [normalizeApiBaseUrl(envBaseUrl)];

  if (
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname)
  ) {
    return ['http://localhost:5005', 'https://hamdanoman.com'];
  }

  return ['https://hamdanoman.com'];
};

const authHeaders = (token?: string | null): HeadersInit => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

export const translateToArabic = async (text: string, token?: string | null) => {
  const sourceText = text.trim();
  if (!sourceText) return '';

  const endpoints = token ? ['/api/translate', '/api/translate-public'] : ['/api/translate-public'];
  let lastError: unknown;

  for (const apiBase of getApiBaseUrls()) {
    for (const endpoint of endpoints) {
      try {
        const response = await fetch(`${apiBase}${endpoint}`, {
          method: 'POST',
          headers: endpoint === '/api/translate' ? authHeaders(token) : authHeaders(),
          body: JSON.stringify({ text: sourceText, targetLang: 'ar' }),
        });

        if (!response.ok) throw new Error(`Translation failed: ${response.status}`);

        const data = await response.json() as { translated?: string };
        if (
          typeof data.translated === 'string' &&
          data.translated !== sourceText &&
          ARABIC_TEXT_PATTERN.test(data.translated)
        ) {
          return data.translated;
        }

        throw new Error('Translation response missing usable Arabic text');
      } catch (error) {
        lastError = error;
      }
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Translation failed');
};

export const translateManyToArabic = async (texts: string[]) => {
  const uniqueTexts = Array.from(new Set(texts.map((text) => text.trim()).filter(Boolean)));
  if (uniqueTexts.length === 0) return {};

  let lastError: unknown;

  for (const apiBase of getApiBaseUrls()) {
    try {
      const response = await fetch(`${apiBase}/api/translate-public`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ text: uniqueTexts, targetLang: 'ar' }),
      });

      if (!response.ok) throw new Error(`Translation failed: ${response.status}`);

      const data = await response.json() as { translations?: TranslationMap };
      if (data.translations) return data.translations;

      throw new Error('Translation response missing translated map');
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Translation failed');
};
